import { BTCNFT, Chain, Contact, ContactSection, NFT, PlugToken } from '../interfaces/common'
import { useCallback, useEffect, useMemo, useState } from 'react'
import useWallet from './useWallet'
import useConfig from './useConfig'
import { useContacts, useLocales, useUserTokens, useWalletProperties } from '../state'
import useBalances from './useBalances'
import useFindTokens from './useFindTokens'
import { ETH_TRANSFER_GAS, useBtcBaseFee, useErc20TransferGas, useETHGasFee } from './useGas'
import type { UTXO } from '../interfaces/walletActions'
import { logger } from '../logger'
import { solanaClient } from '../clients/SolanaClient'
import { formatMoney, formatNumber } from '../utils/formatNumber'
import { useTranslation } from 'react-i18next'
import BitcoinClient from '../clients/BitcoinClient'
import addressMatcher, { AddressType } from '../utils/addressMatcher'
import { TokenBalance } from '../providers/BalanceProvider'
import { decimalsToE8s } from '../utils/validationHelper'
import { GasCalculator } from '../utils/GasCalculator'
import { ICP_LEDGER, TCYCLES_CANISTER_ID, XTC_CANISTER_ID } from '../constants/canisterIds'
import useMintCycles from './useMintCycles'
import useTopup from './useTopup'
import { BTC_ID, ETH_ID, ICP_ICP_ID, ODINBTC_ID } from '../constants/tokenIds'
import { ETH_SUPPORTED_CHAINS } from '../utils/eth'
import { getETHId } from '../utils/getETHId'

export enum SendMode {
  Send = 'send',
  CycleTopup = 'cycleTopup',
}

interface Props {
  walletPrincipals: string[]
  selectedTokenId?: string
  selectedNft?: NFT
  mode?: SendMode
}

export interface Fee {
  token: Pick<PlugToken, 'id' | 'symbol'>
  amount?: number
}

const isValidChainAddress = (chain: Chain, addressType: AddressType) => {
  if (chain === Chain.SOL) {
    return addressType === AddressType.Sol
  } else if (chain === Chain.BTC) {
    return addressType === AddressType.Btc
  } else if (ETH_SUPPORTED_CHAINS.includes(chain)) {
    return addressType === AddressType.Eth
  } else if (chain === Chain.ODIN) {
    return addressType === AddressType.PrincipalId
  } else {
    return addressType === AddressType.AccountId || addressType === AddressType.PrincipalId
  }
}

const TOPUP_TOKENS = [ICP_LEDGER, TCYCLES_CANISTER_ID, XTC_CANISTER_ID]

const useSend = ({ walletPrincipals, selectedTokenId, selectedNft, mode }: Props) => {
  const { t } = useTranslation()

  const [summaryVisible, setSummaryVisible] = useState(false)
  const [toAddress, setToAddress] = useState('')
  const [tokenId, setTokenId] = useState<string>()
  const [nft, setNft] = useState<NFT>()

  const [amount, setAmount] = useState<string>('')
  const [amountCurrency, setAmountCurrency] = useState<string>('')
  const [selectedMode, setSelectedMode] = useState('token')
  const [contactModalVisible, setContactModalVisible] = useState(false)
  const [sendError, setSendError] = useState<string>()
  const [isFindingTokenBalances, setIsFindingTokenBalances] = useState(false)
  const [xtcSendMode, setXtcSendMode] = useState<string>('')

  const { wallet } = useWallet()
  const { nfts } = useConfig()
  const { contacts } = useContacts()
  const { balances, findBalance, refreshBalance, walletNfts } = useBalances()

  const { savedTokens } = useUserTokens()
  const { findTokens } = useFindTokens()
  const { userLocale } = useLocales()

  const token = findBalance({ id: tokenId })

  const { savedCanisters } = useTopup()
  const { setInputAmount: setCyclesInputICP, outAmount: cyclesOutAmount } = useMintCycles()

  const assetChain: Chain | undefined = useMemo(() => {
    if (token?.token.chain) {
      return token.token.chain
    }

    if (nft) {
      return nft.standard === 'ordinal' ? Chain.BTC : Chain.ICP
    }
  }, [token, nft])

  const erc20TransferGas = useErc20TransferGas(
    !!assetChain && ETH_SUPPORTED_CHAINS.includes(assetChain),
    token?.token.address,
    Number(amount) * 10 ** (token?.token.decimals || 0),
    token?.token.chain
  )

  const {
    gasLimit,
    maxFeePerGas,
    maxPriorityFeePerGas,
    failed: gasFailed,
  } = useETHGasFee(
    !!assetChain && ETH_SUPPORTED_CHAINS.includes(assetChain),
    token?.token.chain,
    wallet.ethAddress,
    toAddress,
    Number(amount) * 10 ** (token?.token.decimals || 0),
    true
  )
  const btcBaseFee = useBtcBaseFee(assetChain === 'btc')

  const [utxos, setUtxos] = useState<UTXO[]>([])

  const [wallets, setWallets] = useState<Contact[]>([])
  const { walletNames, walletEmojis } = useWalletProperties()

  useEffect(() => {
    const loadWallets = async () => {
      try {
        setWallets(
          walletPrincipals
            .filter((p) => p.toString() !== wallet.principalId)
            .map((principal) => ({
              avatar: walletEmojis[principal.toString()],
              name: walletNames[principal.toString()] || 'account1',
              address: principal.toString(),
            }))
        )
      } catch (e) {
        logger.error('Failed to get principals', { originalError: e })
      }
    }

    loadWallets()
  }, [walletPrincipals])

  useEffect(() => {
    if (mode === SendMode.CycleTopup && token?.token.address === ICP_LEDGER) {
      setCyclesInputICP(amount)
    } else {
      setCyclesInputICP('')
    }
  }, [mode, setCyclesInputICP, amount, token])

  const filterContact = ({ name, address }: Contact) => {
    const addressType = addressMatcher(address)

    if (assetChain && !isValidChainAddress(assetChain, addressType)) {
      return false
    } else if (toAddress) {
      return (
        (name || '').toLowerCase().includes(toAddress.toLowerCase()) ||
        address.toLowerCase().includes(toAddress.toLowerCase())
      )
    } else {
      return true
    }
  }

  const onTokenSelect = (token: TokenBalance | undefined) => {
    setTokenId(token?.token.id)

    if (token) {
      refreshBalance(token.token)
    }
  }

  useEffect(() => {
    if (selectedTokenId) {
      onTokenSelect(findBalance({ id: selectedTokenId }))
    }

    setNft(selectedNft)
  }, [selectedTokenId, selectedNft, findBalance])

  useEffect(() => {
    if (!wallet.btcAddress || assetChain !== 'btc') return

    BitcoinClient.getUTXOs(wallet.btcAddress).then(setUtxos).catch(logger.debug)
  }, [wallet, assetChain])

  const ethFee = useMemo(() => {
    if (!maxFeePerGas) return

    if (token?.token?.standard === 'ETH' && gasLimit) {
      return gasLimit * maxFeePerGas * 10 ** -18
    } else if (token?.token?.standard === 'ERC20' && erc20TransferGas) {
      return erc20TransferGas * maxFeePerGas * 10 ** -18
    }
  }, [erc20TransferGas, token, maxFeePerGas, gasLimit])

  const btcFee = useMemo(() => {
    if (!btcBaseFee || !toAddress || assetChain !== 'btc' || !(Number(amount) || nft)) return

    const ordinals = walletNfts.flatMap((collection) =>
      collection.nfts.filter((nft) => nft.standard === 'ordinal')
    )

    return BitcoinClient.estimateFee(
      utxos,
      ordinals as BTCNFT[],
      wallet.btcAddress || '',
      toAddress,
      Number(amount) ? parseInt(decimalsToE8s(amount, 8)) : 0,
      btcBaseFee,
      nft as BTCNFT
    )
  }, [utxos, toAddress, amount, wallet, btcBaseFee, walletNfts, assetChain])

  const utxosToUse: UTXO[] = useMemo(() => {
    if (assetChain !== 'btc' || !btcFee) return []

    const ordinals = walletNfts.flatMap((collection) =>
      collection.nfts.filter((nft) => nft.standard === 'ordinal')
    )

    return new GasCalculator(Chain.ETH).utxosForAmount(
      utxos,
      ordinals as BTCNFT[],
      amount ? parseInt(decimalsToE8s(amount, 8)) + btcFee : btcFee,
      nft as BTCNFT
    )
  }, [token, utxos, amount, btcFee, walletNfts, assetChain])

  const fee: Fee | undefined = useMemo(() => {
    if (nft && assetChain === Chain.ICP) {
      return {
        token: { symbol: 'ICP', id: ICP_ICP_ID },
        amount: 0,
      }
    }

    if (ETH_SUPPORTED_CHAINS.includes(assetChain!)) {
      return {
        token: { symbol: 'ETH', id: getETHId(assetChain!) },
        amount: ethFee,
      }
    }

    if (assetChain === 'btc') {
      return {
        token: { symbol: 'BTC', id: BTC_ID },
        amount: (btcFee || 0) * 10 ** -8,
      }
    }

    if (assetChain === 'odin') {
      return {
        token: { symbol: 'BTC', id: ODINBTC_ID },
        amount: 0.0000001, // 10 sats
      }
    }

    if (token && (assetChain === 'icp' || assetChain === 'sol')) {
      return {
        token: { symbol: token.token.symbol, id: token.token.id },
        amount: (token.token.fee || 0) * 10 ** -token.token.decimals,
      }
    }
  }, [token, nft, ethFee, utxosToUse, btcFee, assetChain])

  let maxAmount
  if (token?.token.id === BTC_ID) {
    maxAmount = undefined // max amount depends on fee, but fee depends on amount
  } else if (token?.token.id === fee?.token.id) {
    maxAmount = Math.max(0, (token?.balance || 0) - (fee?.amount || 0))
  } else {
    maxAmount = token?.balance
  }

  const addressType = addressMatcher(toAddress)
  const addressMatched = assetChain
    ? isValidChainAddress(assetChain, addressType)
    : addressType !== AddressType.Invalid

  useEffect(() => {
    if (token?.tokenPrice && selectedMode !== 'currency') {
      const exchangeRate = token.tokenPrice

      if (amount) {
        const currencyAmount = parseFloat(amount) * exchangeRate
        setAmountCurrency(currencyAmount.toFixed(2))
      }
    }
  }, [token, amount, selectedMode])

  let feeAmountCurrency
  if (fee?.amount && fee.token) {
    const feeToken = findBalance({ id: fee.token.id })

    if (feeToken?.tokenPrice) {
      feeAmountCurrency = formatMoney(fee.amount * feeToken.tokenPrice)
    }
  }

  useEffect(() => {
    if (!amountCurrency || !token?.tokenPrice || selectedMode === 'token') return

    const exchangeRate = token.tokenPrice * userLocale.currency.usdExchangeRate
    const newTokenAmount = parseFloat(amountCurrency) / exchangeRate

    setAmount(formatNumber(newTokenAmount, undefined, { useGrouping: false })!)
  }, [token, amountCurrency, selectedMode])

  const error = useMemo(() => {
    if (
      [
        wallet.btcAddress,
        wallet.solAddress,
        wallet.ethAddress,
        wallet.principalId,
        wallet.accountId,
      ].includes(toAddress)
    ) {
      return t('send.errors.ownWallet')
    }

    if (!nft && !Number(amount)) return t('send.errors.noAmount')

    if (token?.token.standard === 'XTC' && mode === SendMode.Send && !xtcSendMode) {
      return t('send.errors.xtcNoMode')
    }

    if (token && parseFloat(amount) > token.balance) {
      return t('send.errors.lowBalance', { token: token.token.symbol })
    }

    if (token && fee?.amount) {
      if (fee.token.id !== token.token.id) {
        const feeTokenBalance = findBalance({ id: fee.token.id })?.balance || 0

        if (fee.amount > feeTokenBalance) {
          return t('send.errors.notEnoughForFee', { token: fee.token.symbol })
        }
      } else if (parseFloat(amount) + fee.amount > token.balance) {
        return t('send.errors.notEnoughForFee', { token: token.token.symbol })
      }
    }

    if (assetChain === 'btc' && !btcFee) {
      // BTC fee calculation fails if the account does not have enough BTC
      return t('send.errors.notEnoughForFee', { token: 'BTC' })
    }

    if (gasFailed) return t('common.errors.gasError')

    if (typeof fee?.amount !== 'number') return t('send.errors.calculatingFee')
  }, [amount, token, t, nft, xtcSendMode, ethFee, addressType, btcFee, assetChain, gasFailed])

  const hasSelectedAsset = useMemo(() => {
    return !!token || !!nft
  }, [token, nft])

  const e8sAmount = useMemo(() => {
    return decimalsToE8s(amount || '', token?.token.decimals)
  }, [amount, token])

  const balancesForSelect = useMemo(() => {
    const relevantBalances = balances.filter(
      (balance) =>
        savedTokens[wallet.principalId]?.includes(balance.token.id) ||
        balance.token.id === ICP_ICP_ID
    )

    if (mode === SendMode.CycleTopup) {
      return relevantBalances.filter((balance) => TOPUP_TOKENS.includes(balance.token.address!))
    }

    if (addressType == AddressType.AccountId)
      return relevantBalances.filter((balance) => balance.token.id === ICP_ICP_ID)

    if (addressType == AddressType.Eth)
      return relevantBalances.filter((balance) =>
        ETH_SUPPORTED_CHAINS.includes(balance.token.chain)
      )

    if (addressType === AddressType.Btc)
      return relevantBalances.filter((balance) => balance.token.chain === Chain.BTC)

    if (addressType === AddressType.Sol)
      return relevantBalances.filter((balance) => balance.token.chain === Chain.SOL)

    return relevantBalances.filter((balance) =>
      [Chain.ICP, Chain.ODIN].includes(balance.token.chain)
    )
  }, [balances, savedTokens, addressType])

  const canSendNft = useMemo(() => {
    return (
      (addressType === AddressType.PrincipalId || addressType === AddressType.Btc) &&
      !hasSelectedAsset &&
      mode === SendMode.Send
    )
  }, [addressType, mode, hasSelectedAsset])

  const showXTCSendMode = useMemo(() => {
    return addressMatched && token?.token.standard === 'XTC' && mode === SendMode.Send
  }, [addressMatched, token, mode])

  const contactSections: ContactSection[] = useMemo(() => {
    let sections: ContactSection[] = []

    if (mode === SendMode.Send) {
      if (!assetChain || assetChain === Chain.ICP || assetChain === Chain.ODIN)
        sections.push({
          kind: 'wallets' as ContactSection['kind'],
          contacts: wallets || [],
        })

      sections.push({
        kind: 'contacts' as ContactSection['kind'],
        contacts,
      })
    }

    if (mode === SendMode.CycleTopup) {
      sections.push({
        kind: 'canisters' as ContactSection['kind'],
        contacts: savedCanisters.map((canister) => ({
          avatar: '⛽️',
          name: canister.name,
          address: canister.id.toString(),
        })),
      })
    }

    sections.forEach((section) => (section.contacts = section.contacts.filter(filterContact)))

    return sections
  }, [contacts, wallets, mode, savedCanisters, assetChain, toAddress])

  const receiverChain = useMemo(() => {
    if (addressType === AddressType.Btc) {
      return Chain.BTC
    } else if (addressType === AddressType.PrincipalId) {
      return Chain.ICP
    }
  }, [addressType])

  const onError = useCallback(
    (error: string) => {
      setSummaryVisible(false)
      setSendError(error)
    },
    [setSendError, setSummaryVisible]
  )

  const validateAndShowSummary = async () => {
    if (assetChain === 'sol' && wallet.solAddress && token) {
      const totalCost = parseFloat(amount!) + (fee?.amount || 0)
      const rent = await solanaClient.calculateAccountRent(wallet.solAddress)
      const rentFee = Number(rent) * 10 ** -token!.token.decimals

      if (token.balance - totalCost !== 0 && token.balance < totalCost + rentFee) {
        return setSendError(t('send.errors.notEnoughSolForRent', { rentFee }))
      }
    }

    setSummaryVisible(true)
  }

  return {
    addressMatched,
    hasSelectedAsset,
    toAddress,
    setToAddress,
    setContactModalVisible,
    addressType,
    nft,
    nfts,
    token,
    balancesForSelect,
    onTokenSelect,
    isFindingTokenBalances,
    setIsFindingTokenBalances,
    findTokens,
    setNft,
    amount,
    setAmount,
    maxAmount,
    selectedMode,
    setSelectedMode,
    amountCurrency,
    feeAmountCurrency,
    setAmountCurrency,
    userLocale,
    xtcSendMode,
    setXtcSendMode,
    fee,
    validateAndShowSummary,
    error,
    summaryVisible,
    setSummaryVisible,
    e8sAmount,
    utxosToUse,
    contactModalVisible,
    sendError,
    setSendError,
    onError,
    wallet,
    walletNfts,
    canSendNft,
    showXTCSendMode,
    cyclesOutAmount,
    contactSections,
    contacts,
    receiverChain,
    maxFeePerGas,
    maxPriorityFeePerGas,
    erc20TransferGas,
    gasLimit,
  }
}

export default useSend
